import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../api/axiosInstance";
import "../style/passbook.css";


const VendorSearch = ({ onSelectVendor, selectedVendor }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [vendors, setVendors] = useState([]);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // 🔥 Call API
  const searchVendors = useCallback(async (name) => {
    if (!name || selectedVendor) {
      setVendors([]);
      return;
    }

    try {
      const response = await api.get(
        "/vendor/getByName",
        {
          params: { name: name }
        }
      );

      setVendors(response.data || []);
    } catch (error) {
      console.error("Vendor search error:", error);
    }
  }, [selectedVendor]);

  // 🔥 Debounce (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      searchVendors(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, searchVendors]);

  // Clear dropdown when vendor is selected
  useEffect(() => {
    if (selectedVendor) {
      setVendors([]);
    }
  }, [selectedVendor]);

  // Calculate dropdown position
  useEffect(() => {
    if (vendors.length > 0 && triggerRef.current) {
      const updatePosition = () => {
        const rect = triggerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const gap = 6;
        const viewportPadding = 8;

        const availableBelow = Math.max(
          0,
          windowHeight - rect.bottom - viewportPadding - gap
        );
        const availableAbove = Math.max(
          0,
          rect.top - viewportPadding - gap
        );

        const preferredHeight = Math.min(320, vendors.length * 60 + 16);
        const showAbove =
          availableBelow < Math.min(preferredHeight, 160) &&
          availableAbove > availableBelow;

        const maxHeight = Math.max(
          1,
          Math.min(preferredHeight, showAbove ? availableAbove : availableBelow)
        );

        const width = Math.min(
          Math.max(rect.width, 320),
          windowWidth - viewportPadding * 2
        );

        let left = rect.left;
        if (left + width > windowWidth - viewportPadding) {
          left = windowWidth - width - viewportPadding;
        }
        left = Math.max(viewportPadding, left);

        let top = showAbove
          ? rect.top - maxHeight - gap
          : rect.bottom + gap;

        top = Math.max(
          viewportPadding,
          Math.min(top, windowHeight - maxHeight - viewportPadding)
        );

        setDropdownStyle({
          position: 'fixed',
          left,
          width,
          maxHeight,
          top,
          zIndex: 'var(--z-dropdown)'
        });
      };

      updatePosition();

      // Add scroll listener to update position during scroll
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [vendors.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setVendors([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (vendor) => {
    setSearchTerm(vendor.displayName || vendor.companyName || vendor.name);
    setVendors([]);
    onSelectVendor(vendor); // send full object to Passbook
  };

  const dropdownContent = vendors.length > 0 && !selectedVendor ? (
    <div className="dropdown-list" style={dropdownStyle} ref={dropdownRef}>
      {vendors.map((vendor) => (
        <div
          key={vendor.id}
          className="dropdown-item"
          onClick={() => handleSelect(vendor)}
        >
          <div className="customer-name">{vendor.displayName || vendor.companyName || vendor.name}</div>
          <div className="customer-contact">
            {vendor.phone || 'No contact'}
          </div>
          <div className="customer-email">
            {vendor.email || 'No email'}
          </div>
        </div>
      ))}
    </div>
  ) : null;
 
  return (
  <div className="customer-search">
    <label>Vendor</label>

    <div className="dropdown-container">
      <div className="customer-input-wrapper" ref={triggerRef}>
        <input
          type="text"
          placeholder="Search vendor..."
          value={searchTerm} 
          onChange={(e) => {
            setSearchTerm(e.target.value);
            onSelectVendor(null);
          }}
          className="customer-input"
        />
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  </div>
  );
};

export default VendorSearch;